import { createPopper } from "@popperjs/core";
import { onMounted, onUnmounted, onUpdated, ref, unref, watch } from "vue";
function on(element, event, handler) {
    if (element && event && handler) {
        element.addEventListener(event, handler, false);
    }
}
function off(element, event, handler) {
    if (element && event) {
        element.removeEventListener(event, handler, false);
    }
}
const defaultTrigger = "hover";
export function usePopperjs(reference, popper, options) {
    const isMounted = ref(false);
    onMounted(() => {
        isMounted.value = true;
    });
    onUnmounted(() => {
        isMounted.value = false;
        destroy();
    });
    const updatedFlag = ref(true);
    onUpdated(() => {
        updatedFlag.value = !updatedFlag.value;
    });
    const referenceRef = ref();
    const popperRef = ref();
    watch(() => [isMounted.value, updatedFlag.value], () => {
        if (!isMounted.value) {
            return;
        }
        if (unref(reference)?.$el) {
            referenceRef.value = unref(reference).$el;
        }
        else {
            referenceRef.value = unref(reference);
        }
        if (unref(popper)?.$el) {
            popperRef.value = unref(popper).$el;
        }
        else {
            popperRef.value = unref(popper);
        }
    });
    const instance = ref();
    watch(() => [referenceRef.value, popperRef.value], () => {
        destroy();
        if (!referenceRef.value) {
            return;
        }
        if (!popperRef.value) {
            return;
        }
        concrete();
    });
    const concrete = () => {
        instance.value = createPopper(referenceRef.value, popperRef.value, {
            placement: options?.placement ?? "bottom",
            modifiers: options?.modifiers ?? [],
            strategy: options?.strategy ?? "absolute",
            onFirstUpdate: options?.onFirstUpdate ?? undefined,
        });
    };
    const destroy = () => {
        instance.value?.destroy();
        instance.value = undefined;
    };
    const visible = ref(false);
    const doToggle = () => (visible.value = !visible.value);
    const doOpen = () => (visible.value = true);
    const doClose = () => (visible.value = false);
    watch(() => [instance.value, unref(options?.trigger), unref(options?.forceShow)], () => {
        if (!instance.value) {
            return;
        }
        if (unref(options?.forceShow)) {
            visible.value = true;
            doOff();
            return;
        }
        doOn();
    });
    watch(() => unref(options?.forceShow), () => {
        if (unref(options?.forceShow)) {
            return;
        }
        if (unref(options?.trigger) === "manual") {
            return;
        }
        visible.value = false;
    });
    watch(() => unref(options?.disabled), () => {
        if (unref(options?.disabled)) {
            doOff();
        }
        else {
            doOn();
        }
    });
    const timer = ref();
    const doMouseover = () => {
        if (unref(options?.delayOnMouseover) === 0) {
            doOpen();
        }
        else {
            clearTimeout(timer.value);
            timer.value = setTimeout(() => {
                doOpen();
            }, unref(options?.delayOnMouseover) ?? 100);
        }
    };
    const doMouseout = () => {
        if (unref(options?.delayOnMouseout) === 0) {
            doClose();
        }
        else {
            clearTimeout(timer.value);
            timer.value = setTimeout(() => {
                doClose();
            }, unref(options?.delayOnMouseout) ?? 100);
        }
    };
    const doOn = () => {
        doOff();
        switch (unref(options?.trigger) ?? defaultTrigger) {
            case "click-to-open": {
                on(referenceRef.value, "click", doOpen);
                on(document, "click", doCloseForDocument);
                break;
            }
            case "click-to-toggle": {
                on(referenceRef.value, "click", doToggle);
                on(document, "click", doCloseForDocument);
                break;
            }
            case "hover": {
                on(referenceRef.value, "mouseover", doMouseover);
                on(referenceRef.value, "mouseout", doMouseout);
                on(referenceRef.value, "mousedown", doMouseout);
                break;
            }
            case "focus": {
                on(referenceRef.value, "focus", doOpen);
                on(referenceRef.value, "blur", doClose);
                break;
            }
            case "manual": {
                break;
            }
            default: {
                throw TypeError();
            }
        }
    };
    const doOff = () => {
        off(referenceRef.value, "click", doOpen);
        off(document, "click", doCloseForDocument);
        off(referenceRef.value, "click", doToggle);
        off(referenceRef.value, "mouseover", doMouseover);
        off(referenceRef.value, "mouseout", doMouseout);
        off(referenceRef.value, "mousedown", doMouseout);
        off(referenceRef.value, "focus", doOpen);
        off(referenceRef.value, "blur", doClose);
    };
    const doCloseForDocument = (e) => {
        if (referenceRef.value?.contains(e.target)) {
            return;
        }
        if (popperRef.value?.contains(e.target)) {
            return;
        }
        doClose();
    };
    watch(() => [instance.value, visible.value], () => {
        if (!instance.value) {
            return;
        }
        if (visible.value || unref(options?.forceShow)) {
            popperRef.value?.classList.remove("vue-use-popperjs-none");
            options?.onShow?.();
            instance.value?.update();
        }
        else {
            popperRef.value?.classList.add("vue-use-popperjs-none");
            options?.onHide?.();
        }
    });
    return {
        instance,
        visible,
    };
}
